import {
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  useTheme,
  Autocomplete,
  createFilterOptions,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import { Add, Delete } from "@mui/icons-material";
import { cloneDeep } from "lodash";
import React, { useEffect, useState } from "react";

import "./RPMDevice.css";

import ConfirmDeleteModal from "components/Modal/ConfirmDeleteModal/ConfirmDeleteModal";
import useDeviceInformation from "components/pages/transmissions/modal/patient/tabs/deviceInformation/useDeviceInformation";
import { MIN_DATE, RPM_DEVICE_OPTIONS } from "static/constants";

import { useGetDeviceAlertListQuery } from "app/store/patient/api/patientApi";
import { getRpmDeviceValueFromKey } from "utils/rpmDevice.util";

import { DatePicker } from "@mui/x-date-pickers";

import { useGetDeviceListQuery } from "app/store/devices/deviceApi";
import dayjs from "dayjs";
import { checkIsAlphaNumeric } from "utils/string";
import { isValidArray } from "utils/array";
import usePatientModal from "../../usePatientModal";
import { FieldArray, Formik } from "formik";
import * as Yup from "yup";
import SubmitButton from "components/Button/SubmitButton";
import { getDateJsFromDateString, getFormattedDate } from "utils/date";
import { DATE_FORMAT } from "static/date";
import { getOptionValue } from "utils/autocomplete";

const filter = createFilterOptions();

const validationSchema = (deviceInformation, isPrimaryDevice) => {
  return Yup.object({
    account: Yup.string().required("Account is required"),
    manufacturer:
      deviceInformation?.rpm_devices?.length > 0
        ? Yup.string().nullable()
        : Yup.string().required("Manufacturer is required"),
    modelName:
      deviceInformation?.rpm_devices?.length > 0
        ? Yup.string().nullable()
        : Yup.string().required("Model name is required"),
    modelNumber:
      deviceInformation?.rpm_devices?.length > 0
        ? Yup.string().nullable()
        : Yup.string().required("Serial number is required"),
    doi:
      isPrimaryDevice && deviceInformation?.rpm_devices?.length === 0
        ? Yup.string()
            .required("Date of Implant is required")
            .test(
              "is-valid-date",
              "Date of Implant must be a valid date",
              (value) => {
                if (!value) return false; // Required field validation
                const parsedDate = new Date(value);
                return !isNaN(parsedDate.getTime()); // Check if it's a valid date
              }
            )
        : Yup.string().nullable(),
    mriCompatible: Yup.boolean(),
    implanting_physicians: Yup.string(),
    alertDeviceText: Yup.string(),
    alertDevice: Yup.boolean(),
    rpmDevices: Yup.array().of(
      Yup.object().shape({
        imei: Yup.string().required("IMEI is required"),
        rpmDeviceName: Yup.string().required("RPM Device type is required"),
      })
    ),
  });
};

const DeviceInformationForm = ({
  isPrimaryDevice,
  handleCancelClick,
  handleSubmit,
  isLoading: fieldLoading,
}) => {
  const theme = useTheme();

  const {
    handleInputChange,

    profile,
    accounts,
    primaryDevice,
    secondaryDevice,
    manufacturerList,
    isSaasAccountUser,
    newDeviceInformation,
    newSecondaryDeviceInformation,
  } = useDeviceInformation(isPrimaryDevice);

  const deviceInformation = isPrimaryDevice ? primaryDevice : secondaryDevice;
  const newDeviceInformationValue = isPrimaryDevice
    ? newDeviceInformation
    : newSecondaryDeviceInformation;

  const { physicianData } = usePatientModal();
  const { data: deviceList } = useGetDeviceListQuery();
  const { data: deviceAlertList, isLoading } = useGetDeviceAlertListQuery(
    {},
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const rpmDeviceInitialValue = {
    imei: "",
    active: true,
    rpmDeviceName: "",
  };

  const [rpmIndexToDelete, setRpmIndexToDelete] = useState();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rpmDeviceFormData, setRpmDeviceFormData] = useState([]);
  const [secondaryRpmDeviceFormData, setSecondaryRpmDeviceFormData] = useState(
    []
  );

  const useStyles = makeStyles({
    switchRoot: {
      marginLeft: "-10px",
    },
  });

  const classes = useStyles();

  useEffect(() => {
    if (newDeviceInformation) {
      const { rpm_devices: rpmDevices = [] } = newDeviceInformationValue;
      handleUpdateRpmDeviceFromData(rpmDevices);
    }
  }, [newDeviceInformation]);

  const handleRpmFormChange = (index, event, dataKey = undefined) => {
    const key = event?.target?.name ?? dataKey;
    let value = event?.target?.value ?? event;

    if (key === "active" && value === "") {
      value = false;
    }

    const rpmDeviceData = [...getRpmDeviceFormData()];
    const rpmDevice = { ...rpmDeviceData[index] };

    if (key === "rpmDeviceName") {
      rpmDevice.rpmDeviceKey = value;
      value = getRpmDeviceValueFromKey(value);
    }

    if (key === "imei") {
      rpmDevice.imei = value;
    }

    rpmDevice[key] = value;

    const updatedRpmDeviceData = [...rpmDeviceData];
    updatedRpmDeviceData[index] = rpmDevice;
    handleUpdateRpmDeviceFromData(updatedRpmDeviceData);
    handleRPMDeviceReduxUpdate(updatedRpmDeviceData);
  };

  const handleRPMDeviceReduxUpdate = (rpmDeviceData) => {
    const newDeviceInformationCopy = {
      ...newDeviceInformationValue,
      rpm_devices: [...rpmDeviceData],
    };
    handleInputChange({ isPrimaryDevice, values: newDeviceInformationCopy });
  };

  const handleUpdateRpmDeviceFromData = (formData) => {
    isPrimaryDevice
      ? setRpmDeviceFormData(formData)
      : setSecondaryRpmDeviceFormData(formData);
  };

  const handleAddRpmDevice = () => {
    const newRpmDeviceData = rpmDeviceInitialValue;
    const updatedRpmDeviceData = [...getRpmDeviceFormData(), newRpmDeviceData];
    handleUpdateRpmDeviceFromData(updatedRpmDeviceData);
    handleRPMDeviceReduxUpdate(updatedRpmDeviceData);
  };

  const handleDeleteRpmDevice = (index) => {
    const rpmDeviceFormDataCopy = [...getRpmDeviceFormData()];
    rpmDeviceFormDataCopy.splice(index, 1);

    handleUpdateRpmDeviceFromData(rpmDeviceFormDataCopy);
    handleRPMDeviceReduxUpdate(rpmDeviceFormDataCopy);

    setShowDeleteModal(false);
  };

  const getRpmDeviceFormData = () => {
    const rpmDeviceFormDataValue = isPrimaryDevice
      ? rpmDeviceFormData
      : secondaryRpmDeviceFormData;
    return isValidArray(rpmDeviceFormDataValue) ? rpmDeviceFormDataValue : [];
  };

  const getRpmDeleteBodyText = () => {
    const rpmToDelete = getRpmDeviceFormData()?.[rpmIndexToDelete];
    const { rpmDeviceName, imei } = rpmToDelete;
    const confirmDeleteText = `Are you sure you want to delete: ${rpmDeviceName} - ${imei}?`;
    return confirmDeleteText;
  };

  const handleRpmDelete = (index) => {
    const rpmToDelete = rpmDeviceFormData[index];
    const { rpmDeviceName, imei } = rpmToDelete;

    if (!rpmDeviceName || !imei) {
      return handleDeleteRpmDevice(index);
    }
    setRpmIndexToDelete(index);
    setShowDeleteModal(true);
  };

  const getPhysicianList = () => {
    const filteredPhysicianData = physicianData?.filter(
      (physician) =>
        physician.accounts.includes(
          isPrimaryDevice ? profile.account : newDeviceInformationValue?.account
        ) &&
        !newDeviceInformationValue?.implanting_physicians?.includes(
          physician.name
        )
    );
    return filteredPhysicianData?.map(({ name }) => ({
      title: name,
      inputValue: name,
    }));
  };

  return (
    <>
      <Formik
        initialValues={{
          account: newDeviceInformationValue?.account || "",
          manufacturer: newDeviceInformationValue?.manufacturer || "",
          modelName: newDeviceInformationValue?.modelName || "",
          modelNumber: newDeviceInformationValue?.modelNumber || "",
          doi: newDeviceInformationValue?.doi || "",
          mriCompatible: newDeviceInformationValue?.mriCompatible || false,
          implanting_physicians:
            newDeviceInformationValue?.implanting_physicians?.[0] || "",
          alertDeviceText: newDeviceInformationValue?.alertDeviceText || "",
          alertDevice: newDeviceInformationValue?.alertDevice || false,
          rpmDevices: getRpmDeviceFormData(),
        }}
        validationSchema={validationSchema(
          newDeviceInformationValue,
          isPrimaryDevice
        )}
        onSubmit={() => handleSubmit(isPrimaryDevice)}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => {
          return (
            <>
              <Grid
                container
                spacing={2}
                alignItems="flex-start"
                marginBottom={2}
              >
                <Grid item xs={4}>
                  {isPrimaryDevice ? (
                    <TextField
                      required
                      fullWidth
                      margin="none"
                      variant="outlined"
                      name="account"
                      label="Account"
                      value={deviceInformation?.account}
                      disabled
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.account && !!errors.account}
                      helperText={touched.account && errors.account}
                    />
                  ) : (
                    <>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        margin="none"
                        error={touched.account && !!errors.account}
                      >
                        <InputLabel
                          id="demo-simple-select-label"
                          style={{ background: "white" }}
                        >
                          Account
                        </InputLabel>
                        <Select
                          style={{ textAlign: "left" }}
                          id="state"
                          name="account"
                          value={newDeviceInformationValue?.account || ""}
                          onBlur={handleBlur}
                          onChange={(e) => {
                            const value = e.target.value;

                            const copy = {
                              ...newDeviceInformationValue,
                              account: value,
                            };
                            handleInputChange({
                              isPrimaryDevice,
                              values: copy,
                            });
                            setFieldValue("account", value);
                          }}
                        >
                          {accounts &&
                            accounts.map((account, index) => {
                              return (
                                <MenuItem key={index} value={account?.name}>
                                  {account?.name}
                                </MenuItem>
                              );
                            })}
                        </Select>
                      </FormControl>
                      {touched.account && errors.account && (
                        <FormHelperText error>{errors.account}</FormHelperText>
                      )}
                    </>
                  )}
                </Grid>
                <Grid item xs={4}>
                  {isPrimaryDevice ? (
                    <TextField
                      required
                      fullWidth
                      margin="none"
                      variant="outlined"
                      name="manufacturer"
                      label="Manufacturer"
                      value={deviceInformation?.manufacturer}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.manufacturer && !!errors.manufacturer}
                      helperText={touched.manufacturer && errors.manufacturer}
                      disabled
                    />
                  ) : (
                    <>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        margin="none"
                        error={touched.manufacturer && !!errors.manufacturer}
                      >
                        <InputLabel
                          id="manufacturer-label"
                          style={{ background: "white" }}
                        >
                          Manufacturer
                        </InputLabel>
                        <Select
                          id="manufacturer"
                          value={newDeviceInformationValue?.manufacturer || ""}
                          name="manufacturer"
                          onBlur={handleBlur}
                          onChange={(e) => {
                            const value = e.target.value;
                            const copy = {
                              ...newDeviceInformationValue,
                              manufacturer: value,
                            };
                            handleInputChange({
                              isPrimaryDevice,
                              values: copy,
                            });
                            setFieldValue("manufacturer", value);
                          }}
                          readOnly={isLoading}
                        >
                          {manufacturerList?.map((manufacturer) => (
                            <MenuItem
                              key={manufacturer.name}
                              value={manufacturer.name}
                            >
                              {manufacturer.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {touched.manufacturer && errors.manufacturer && (
                        <FormHelperText error>
                          {errors.manufacturer}
                        </FormHelperText>
                      )}
                    </>
                  )}
                </Grid>
                <Grid item xs={4}>
                  <FormControl margin="none" fullWidth>
                    <Autocomplete
                      name="modelName"
                      fullWidth
                      clearOnBlur
                      selectOnFocus
                      handleHomeEndKeys
                      value={
                        deviceList?.find(
                          (item) =>
                            item?.modelDisplayName ===
                            newDeviceInformationValue?.modelName
                        ) || null
                      }
                      onBlur={handleBlur}
                      filterOptions={(options, params) =>
                        filter(options, params)
                      }
                      options={
                        deviceList?.filter((item) => item?.modelDisplayName) ??
                        []
                      }
                      getOptionLabel={(option) =>
                        option?.modelDisplayName || ""
                      }
                      onChange={(_event, newValue) => {
                        const selectedValue = newValue?.modelDisplayName || "";
                        const copy = {
                          ...newDeviceInformationValue,
                          modelName: selectedValue,
                        };
                        handleInputChange({ isPrimaryDevice, values: copy });
                        setFieldValue("modelName", selectedValue);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label="Model Name"
                          variant="outlined"
                          placeholder="Model Name"
                          error={touched.modelName && !!errors.modelName}
                          helperText={touched.modelName && errors.modelName}
                          InputProps={{ ...params.InputProps, readOnly: false }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    required
                    fullWidth
                    margin="none"
                    variant="outlined"
                    name="modelNumber"
                    label="Serial Number"
                    value={newDeviceInformationValue?.modelNumber}
                    inputProps={{ maxLength: 50 }}
                    onBlur={handleBlur}
                    error={touched.modelNumber && !!errors.modelNumber}
                    helperText={touched.modelNumber && errors.modelNumber}
                    onChange={(event) => {
                      const value = event.target.value;
                      const copy = cloneDeep(newDeviceInformationValue);

                      if (value === "" || checkIsAlphaNumeric(value)) {
                        copy.modelNumber = value;
                        handleInputChange({ isPrimaryDevice, values: copy });
                        setFieldValue("modelNumber", value);
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth margin="none">
                    <DatePicker
                      margin="none"
                      autoOk
                      inputVariant="outlined"
                      fullWidth
                      id="doi"
                      label="Date of Implant"
                      name="doi"
                      required={isPrimaryDevice}
                      className="trans-table__filterDatePicker"
                      format="MM/DD/YYYY"
                      KeyboardButtonProps={{
                        "aria-label": "pick date",
                      }}
                      minDate={dayjs(MIN_DATE)}
                      maxDate={dayjs(new Date())}
                      value={
                        newDeviceInformationValue?.doi
                          ? getDateJsFromDateString(
                              newDeviceInformationValue.doi
                            )
                          : null
                      }
                      onBlur={handleBlur}
                      onChange={(date) => {
                        const formattedDate = getFormattedDate(
                          date,
                          DATE_FORMAT.DEFAULT
                        );
                        const copy = {
                          ...newDeviceInformationValue,
                          doi: formattedDate,
                        };
                        handleInputChange({
                          isPrimaryDevice,
                          values: copy,
                        });
                        setFieldValue("doi", formattedDate);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required:
                            isPrimaryDevice &&
                            deviceInformation?.rpm_devices?.length === 0,
                          error: touched.doi && Boolean(errors.doi),
                          helperText: touched.doi && errors.doi,
                        },
                      }}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={4}>
                  <FormControl fullWidth variant="outlined" margin="none">
                    <InputLabel
                      id="demo-simple-select-label"
                      style={{ background: "white" }}
                    >
                      MRI Compatible
                    </InputLabel>
                    <Select
                      style={{ textAlign: "left" }}
                      id="mriCompatible"
                      value={newDeviceInformationValue?.mriCompatible}
                      name="mriCompatible"
                      variant="outlined"
                      onBlur={handleBlur}
                      onChange={(event) => {
                        const value = event.target.value;

                        const copy = cloneDeep(newDeviceInformationValue);
                        copy.mriCompatible = value;

                        handleInputChange({ isPrimaryDevice, values: copy });
                        setFieldValue("mriCompatible", value);
                      }}
                    >
                      <MenuItem value={true}>Yes</MenuItem>
                      <MenuItem value={false}>No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item sm={4}>
                  <FormControl fullWidth variant="outlined" margin="none">
                    <Autocomplete
                      freeSolo
                      id="implantingPhysicians"
                      name="implanting_physicians"
                      value={
                        newDeviceInformationValue?.implanting_physicians?.[0] ||
                        ""
                      }
                      options={
                        isValidArray(physicianData) ? getPhysicianList() : []
                      }
                      openOnFocus={true}
                      autoSelect
                      onChange={(e, newValue) => {
                        let physician;

                        // Handle custom option for "+ Add"
                        if (
                          newValue &&
                          typeof newValue === "object" &&
                          newValue.inputValue
                        ) {
                          physician = newValue.inputValue;
                        } else {
                          physician = getOptionValue(newValue);
                        }

                        const copy = cloneDeep(newDeviceInformationValue);
                        copy.implanting_physicians = [physician];
                        handleInputChange({ isPrimaryDevice, values: copy });
                        setFieldValue("implanting_physicians", physician);
                      }}
                      filterOptions={(options, params) => {
                        const filtered = filter(options, params);

                        if (params.inputValue !== "") {
                          filtered.unshift({
                            inputValue: params.inputValue,
                            label: `+ Add "${params.inputValue}"`,
                          });
                        }

                        return filtered;
                      }}
                      getOptionLabel={getOptionValue}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Implanting Physician"
                          variant="outlined"
                          placeholder="Implanting Physician"
                        />
                      )}
                    />
                  </FormControl>
                </Grid>

                <Grid item sm={4}>
                  {/* change to select */}
                  <FormControl fullWidth variant="outlined" margin="none">
                    <Autocomplete
                      freeSolo
                      id="alertDeviceText"
                      name="alertDeviceText"
                      value={newDeviceInformationValue?.alertDeviceText}
                      filterSelectedOptions
                      limitTags={2}
                      options={
                        deviceAlertList?.map((item) => ({
                          label: item,
                          inputValue: item,
                        })) || []
                      }
                      openOnFocus={true}
                      onBlur={handleBlur}
                      onChange={(e, newValue) => {
                        let alertDeviceText;

                        // Handle custom option for "+ Add"
                        if (
                          newValue &&
                          typeof newValue === "object" &&
                          newValue.inputValue
                        ) {
                          alertDeviceText = newValue.inputValue;
                        } else {
                          alertDeviceText = getOptionValue(newValue);
                        }

                        const copy = {
                          ...newDeviceInformationValue,
                          alertDeviceText,
                        };
                        handleInputChange({ isPrimaryDevice, values: copy });
                        setFieldValue("alertDeviceText", newValue);
                      }}
                      filterOptions={(options, params) => {
                        const filtered = filter(options, params);
                        if (params.inputValue !== "") {
                          filtered.unshift({
                            inputValue: params.inputValue,
                            label: `+ Add "${params.inputValue}"`,
                          });
                        }

                        return filtered;
                      }}
                      clearOnBlur
                      getOptionLabel={getOptionValue}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Device Alert"
                          variant="outlined"
                          placeholder="Device Alert"
                          required
                          InputProps={{
                            ...params.InputProps,
                            readOnly: isLoading ? true : false,
                          }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid
                  item
                  sm={4}
                  style={{ textAlign: "left" }}
                  alignSelf="center"
                >
                  <FormControlLabel
                    label="Device Alert"
                    control={
                      <Switch
                        name="alertDevice"
                        checked={newDeviceInformationValue?.alertDevice}
                        onBlur={handleBlur}
                        onChange={(event, checked) => {
                          const copy = {
                            ...newDeviceInformationValue,
                            alertDevice: checked,
                          };
                          handleInputChange({ isPrimaryDevice, values: copy });
                          setFieldValue("alertDevice", checked);
                        }}
                      />
                    }
                  />
                </Grid>
              </Grid>

              {!isSaasAccountUser && isPrimaryDevice && (
                <Grid container={2}>
                  <Grid item xs={12}>
                    <div className="patient-form__info--header mb-0">
                      RPM Device
                    </div>
                  </Grid>

                  <FieldArray
                    name="rpmDevices"
                    render={({ push, remove }) => (
                      <>
                        <Grid container spacing={2}>
                          {values?.rpmDevices?.map((data, index) => {
                            const { imei, active, rpmDeviceKey } = data;
                            return (
                              <>
                                <Grid item style={{ width: "40%" }}>
                                  <FormControl
                                    fullWidth
                                    variant="outlined"
                                    margin="none"
                                  >
                                    <InputLabel
                                      id="demo-simple-select-label"
                                      style={{ background: "white" }}
                                    >
                                      RPM Device Type
                                    </InputLabel>
                                    <Select
                                      style={{ textAlign: "left" }}
                                      id="rpmDeviceName"
                                      value={rpmDeviceKey || ""}
                                      name="rpmDeviceName"
                                      variant="outlined"
                                      error={
                                        touched?.rpmDevices?.[index]
                                          ?.rpmDeviceName &&
                                        !!errors?.rpmDevices?.[index]
                                          ?.rpmDeviceName
                                      }
                                      onChange={(event) => {
                                        handleRpmFormChange(index, event);
                                        setFieldValue(
                                          `rpmDevices[${index}].rpmDeviceName`,
                                          event.target.value
                                        );
                                      }}
                                    >
                                      {RPM_DEVICE_OPTIONS.map(
                                        (rpmDevice, index) => {
                                          const isSelected =
                                            getRpmDeviceFormData().find(
                                              (rpm) =>
                                                rpm.rpmDeviceKey ===
                                                rpmDevice.key
                                            );
                                          return (
                                            <MenuItem
                                              value={rpmDevice.key}
                                              key={index}
                                              disabled={isSelected}
                                            >
                                              {rpmDevice.value}
                                            </MenuItem>
                                          );
                                        }
                                      )}
                                    </Select>
                                  </FormControl>
                                  {touched?.rpmDevices?.[index]
                                    ?.rpmDeviceName &&
                                    errors?.rpmDevices?.[index]
                                      ?.rpmDeviceName && (
                                      <FormHelperText error>
                                        {
                                          errors?.rpmDevices?.[index]
                                            ?.rpmDeviceName
                                        }
                                      </FormHelperText>
                                    )}
                                </Grid>

                                <Grid item style={{ width: "40%" }}>
                                  <TextField
                                    required
                                    fullWidth
                                    margin="none"
                                    variant="outlined"
                                    name="imei"
                                    label="IMEI"
                                    value={imei}
                                    onInput={(event) => {
                                      const input = event.target;
                                      if (input.value.length > 50) {
                                        input.value = input.value.slice(0, 50); // Truncate extra characters
                                      }
                                    }}
                                    onChange={(event) => {
                                      const value = event.target.value;
                                      // Accept only numeric values
                                      if (/^\d*$/.test(value)) {
                                        handleRpmFormChange(index, event);
                                        setFieldValue(
                                          `rpmDevices[${index}].imei`,
                                          value
                                        );
                                      }
                                    }}
                                    error={
                                      touched?.rpmDevices?.[index]?.imei &&
                                      !!errors?.rpmDevices?.[index]?.imei
                                    }
                                    helperText={
                                      touched?.rpmDevices?.[index]?.imei &&
                                      errors?.rpmDevices?.[index]?.imei
                                    }
                                  />

                                  {/* TODO: need to discuss if this is required still */}
                                  {/* {isImeiRemovedByUser && (
                    <span className="error">
                      Note: Leaving field this empty will delete your RPM
                      Devices from the system.
                    </span>
                  )} */}
                                </Grid>

                                <Grid
                                  container
                                  columnSpacing={2}
                                  alignItems="flex-start"
                                  justifyContent="flex-start"
                                  style={{
                                    width: theme.breakpoints.down("sm")
                                      ? "20%"
                                      : "15%",
                                    paddingLeft: "24px",
                                    marginTop: "24px",
                                  }}
                                >
                                  <Grid
                                    item
                                    display="flex"
                                    alignItems="center"
                                    gap={2}
                                  >
                                    <FormControlLabel
                                      label="Active"
                                      labelPlacement="start"
                                      control={
                                        <Switch
                                          name="deviceActive"
                                          checked={active || false}
                                          className={classes.switchRoot}
                                          onChange={() => {
                                            handleRpmFormChange(
                                              index,
                                              !active,
                                              "active"
                                            );

                                            setFieldValue(
                                              `rpmDevices[${index}].active`,
                                              !active
                                            );
                                          }}
                                        />
                                      }
                                      style={{
                                        gap: "6px",
                                        marginLeft: 0,
                                      }}
                                    />

                                    {rpmDeviceFormData.length >= 1 && (
                                      <Delete
                                        className=" rpm-device-information__delete"
                                        onClick={() => {
                                          handleRpmDelete(index);
                                          remove(index);
                                        }}
                                      />
                                    )}
                                  </Grid>
                                </Grid>
                              </>
                            );
                          })}
                        </Grid>
                        {isPrimaryDevice &&
                          rpmDeviceFormData?.length <
                            RPM_DEVICE_OPTIONS.length && (
                            <Grid item>
                              <span
                                className="rpm-device-information__add-rpm-device mt-2"
                                onClick={() => {
                                  handleAddRpmDevice();
                                  push({ ...rpmDeviceInitialValue });
                                }}
                              >
                                <Add className="rpm-device-information__add-icon" />
                                <span className="rpm-device-information__add-text">
                                  Add RPM Device
                                </span>
                              </span>
                            </Grid>
                          )}
                      </>
                    )}
                  />
                </Grid>
              )}

              <div
                className="d-flex flex-end mt-2"
                style={{ textAlign: "right", gap: "10px", display: "flex" }}
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleCancelClick}
                >
                  Cancel
                </Button>
                <SubmitButton
                  handleClick={handleSubmit}
                  disabled={fieldLoading}
                  buttonText={fieldLoading ? "Savings..." : "Save"}
                />
              </div>
            </>
          );
        }}
      </Formik>
      {showDeleteModal && (
        <ConfirmDeleteModal
          isLoading={false}
          isModalOpen={showDeleteModal}
          modalBodyText={getRpmDeleteBodyText()}
          onClose={() => setShowDeleteModal(false)}
          onDelete={() => handleDeleteRpmDevice(rpmIndexToDelete)}
        />
      )}
    </>
  );
};

export default DeviceInformationForm;
